import { createContext, useContext } from "react";
import { runInAction, makeAutoObservable } from "mobx";
import {
  Category,
  Coordinate,
  Event,
  Organization,
} from "@root/utils/entities";
import {
  DateRange,
  FilterType,
  MapBounds,
  NumberRange,
} from "@root/utils/types";
import { eventService } from "@root/services/api/eventService";
import {
  getApiError,
  hasErrors,
  parseEventFromBackend,
} from "@root/utils/functions";
import { ModalStore } from "./modalStore";
import { EventSearchDefaults } from "@root/utils/enums";
import { PaginationStore } from "./paginationStore";

type ViewMode = "list" | "map";
export type EventSearchFilters = {
  address?: string;
  mileRadius: number;
  ageRange: NumberRange;
  dateRange?: DateRange;
  categories?: string[];
  coordinate?: Coordinate;
};

export class EventPageStore {
  private readonly _defaultFilters: EventSearchFilters = {
    address: "",
    mileRadius: EventSearchDefaults.MileRadius,
    ageRange: {
      min: EventSearchDefaults.MinAge,
      max: EventSearchDefaults.MaxAge,
    },
  };

  readonly paginationStore;
  readonly viewModalStore;
  readonly orgModalStore;

  isLoading: boolean = false;
  isSearching: boolean = false;
  fitMap: boolean = false;
  zoomLevel?: number;
  error?: string;
  categories: Category[];
  events?: Event[];
  filters: EventSearchFilters;
  mapBounds?: MapBounds;
  viewMode: ViewMode = "map";
  event?: Event;
  organization?: Organization;
  currentSubview?: "event" | "organization";
  userId?: string;

  // Increments per search; responses from older searches are dropped so a slow
  // earlier request can't overwrite newer results (e.g. after quick zooms).
  private _searchSeq = 0;

  constructor() {
    makeAutoObservable(this);
    this.filters = this._defaultFilters;
    this.categories = [];
    this.events = [];
    this.paginationStore = new PaginationStore();
    this.viewModalStore = new ModalStore();
    this.orgModalStore = new ModalStore();
  }

  showLoading() {
    this.isLoading = true;
  }

  hideLoading() {
    this.isLoading = false;
  }

  showError(error?: string) {
    this.error = error;
    this.isLoading = false;
  }

  setUserId(userId?: string) {
    this.userId = userId;
  }

  searchEvents(
    userLocation?: Coordinate,
    fitMap: boolean = false,
    zoomLevel?: number,
  ) {
    // Follow state comes from the API: GET /events sets `isFollowed` per event
    // for the signed-in user, so no extra GET /users/{id}/events/following.
    const seq = ++this._searchSeq;
    this.isSearching = true;

    eventService
      .searchEvents(
        this.paginationStore.page,
        this.filters.address,
        this.filters.mileRadius,
        this.filters.ageRange,
        this.filters.dateRange,
        this.filters.categories,
        this.mapBounds,
        userLocation,
      )
      .then(async (response) => {
        if (seq !== this._searchSeq) return; // superseded by a newer search
        runInAction(() => {
          if (hasErrors(response)) {
            this.error = getApiError(response)?.message;
          } else {
            this.error = undefined;

            const rawEvents = response.data?.events ?? [];
            // Parse events from backend format
            const events = rawEvents.map((e: any) => parseEventFromBackend(e));
            const searchFilters = response.data?.searchFilters;
            const pagination = response.data?.pagination;

            this.events = events;

            this.filters.coordinate = searchFilters?.coordinate;

            this.fitMap = fitMap;
            this.zoomLevel = zoomLevel;
            this.paginationStore.setPagination(
              pagination?.page ?? 0,
              pagination?.pageCount ?? 0,
              pagination?.totalCount ?? 0,
            );
          }
          this.isSearching = false;
        });
      });
  }

  loadCategories() {
    eventService.getCategories().then((response) =>
      runInAction(() => {
        if (hasErrors(response)) {
          this.error = getApiError(response)?.message;
          this.categories = [];
        } else {
          this.categories = response.data?.categories ?? [];
        }
      }),
    );
  }

  setFilters(
    filterType: FilterType,
    filters: EventSearchFilters,
    userLocation?: Coordinate,
    search: boolean = false,
    fitMap: boolean = false,
  ) {
    this.filters = filters;

    if (filterType === "location") {
      this.mapBounds = undefined;
    }

    if (search) this.searchEvents(userLocation, fitMap);
  }

  setMapBounds(northEast: Coordinate, southWest: Coordinate) {
    if (
      this.mapBounds?.northEast.latitude === northEast.latitude &&
      this.mapBounds?.northEast.longitude === northEast.longitude &&
      this.mapBounds?.southWest.latitude === southWest.latitude &&
      this.mapBounds?.southWest.longitude === southWest.longitude
    )
      return;

    this.mapBounds = { northEast, southWest };
    this.fitMap = false;

    this.searchEvents();
  }

  showList() {
    this.viewMode = "list";
  }

  showMap() {
    this.viewMode = "map";
  }

  toggleViewMode() {
    if (this.viewMode === "map") this.showList();
    else this.showMap();
  }

  toggleFitMap(val: boolean) {
    this.fitMap = val;
    if (this.fitMap) this.mapBounds = undefined;
  }

  showEvent(event: Event) {
    this.event = event;
    this.currentSubview = this.organization ? "organization" : "event";
    this.viewModalStore.open();
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.set("event", event.id);
      window.history.replaceState(null, "", url.toString());
    }
    const eventId = event.id;
    eventService.getEvent(eventId).then((response) => {
      runInAction(() => {
        if (
          !hasErrors(response) &&
          response.data?.event &&
          this.event?.id === eventId
        ) {
          this.event = parseEventFromBackend(response.data.event);
        }
      });
    });
  }

  hideEvent() {
    this.viewModalStore.close();
    this.event = undefined;
    if (this.organization) {
      this.currentSubview = "organization";
      this.orgModalStore?.open();
    } else {
      this.currentSubview = undefined;
    }
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("event");
      window.history.replaceState(null, "", url.toString());
    }
  }

  showOrganization(organization?: Organization) {
    this.organization = organization;
    this.currentSubview = this.event ? "event" : "organization";
    this.orgModalStore?.open();
  }

  hideOrganization() {
    this.orgModalStore?.close();
    this.organization = undefined;
    this.currentSubview = this.event ? "event" : undefined;
  }

  removeEvent(eventId: string) {
    if (this.events) {
      this.events = this.events.filter((event) => event.id !== eventId);
    }
  }

  // Sync follow state back into the list when it is changed from the detail view.
  setEventFollowState(eventId: string, isFollowed: boolean) {
    if (this.events) {
      const target = this.events.find((event) => event.id === eventId);
      if (target) {
        target.isFollowed = isFollowed;
        // Reassign to a new array reference so observers re-render the list.
        this.events = [...this.events];
      }
    }
    if (this.event?.id === eventId) {
      this.event.isFollowed = isFollowed;
    }
  }
}

export const EventPageContext = createContext<EventPageStore>(null!);
export const useEventPageContext = () => useContext(EventPageContext);
